// frontend/admin-portal/src/components/sales/ProductPicker.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner, formatMoney } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";

/**
 * Searchable product picker.
 * - Searches by name, SKU, or category in a single input
 * - Shows a suggestlist under the input
 * - Click a suggestion → calls onPick(product)
 * - Keyboard: ↑/↓ to move, Enter to pick, Esc to close
 * - Debounced backend search (300ms)
 */
export default function ProductPicker({
  onPick,
  placeholder = "Search products by name, SKU, or category…",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { items } = await productService.list({
          search: query || undefined,
          limit: 25,
          isActive: true,
        });
        setResults(items || []);
        setActiveIndex(items && items.length ? 0 : -1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (p) => {
    onPick?.(p);
    setQuery("");
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) pick(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showList = open && (loading || results.length > 0 || query);

  return (
    <div className="fh-product-picker" ref={wrapRef}>
      <div className="fh-product-picker__input-wrap">
        <span className="fh-product-picker__icon" aria-hidden>
          🔍
        </span>
        <input
          ref={inputRef}
          type="text"
          className="fh-input fh-product-picker__input"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {loading && (
          <span className="fh-product-picker__spinner">
            <Spinner size="sm" />
          </span>
        )}
      </div>

      {showList && (
        <div className="fh-product-picker__list" role="listbox">
          {loading && results.length === 0 && (
            <div className="fh-product-picker__empty">Searching…</div>
          )}
          {!loading && results.length === 0 && query && (
            <div className="fh-product-picker__empty">
              No products match "{query}"
            </div>
          )}
          {results.map((p, i) => (
            <button
              type="button"
              key={p.id}
              role="option"
              aria-selected={i === activeIndex}
              className={`fh-product-picker__item ${
                i === activeIndex ? "is-active" : ""
              }`}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault(); // keep input focus
                pick(p);
              }}
            >
              <div className="fh-product-picker__item-main">
                <div className="fh-product-picker__item-title">{p.name}</div>
                <div className="fh-product-picker__item-sub">
                  <code>{p.sku}</code>
                  {p.category?.name && <span>· {p.category.name}</span>}
                  {p.brand?.name && <span>· {p.brand.name}</span>}
                </div>
              </div>
              <div className="fh-product-picker__item-right">
                <div className="fh-product-picker__item-price">
                  {formatMoney(p.price, { symbol: p.currency || "USD" })}
                </div>
                <div className="fh-product-picker__item-stock">
                  Stock: {p.stock} {p.unit}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
